import { MenuItem, Stack, type SxProps, type Theme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Show, TextField } from '@side/pantry';
import { isValid } from 'date-fns';
import type { FocusEvent } from 'react';
import { DatePickerWithEventTracking } from '@/components/DatePicker/DatePickerV2';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import {
  DATE_RANGE_OPTION_LABELS,
  DATE_RANGE_OPTIONS,
  type DateInput,
} from '../types';
import { getEndDate, getStartDate } from './DatesFilter.helpers';

const DATE_RANGE_OPTIONS_LIST = [
  DATE_RANGE_OPTIONS.DEFAULT,
  DATE_RANGE_OPTIONS.LTM,
  DATE_RANGE_OPTIONS.LTD,
  DATE_RANGE_OPTIONS.YTD,
  DATE_RANGE_OPTIONS.CUSTOM,
] as const;

interface Props {
  selectedDateRange: DATE_RANGE_OPTIONS;
  endDate: DateInput;
  onChangeDateRange: (selectedDateRange: DATE_RANGE_OPTIONS) => void;
  onChangeEndDate: (date: DateInput | null) => void;
  onChangeStartDate: (date: DateInput | null) => void;
  startDate: DateInput;
  sx?: SxProps<Theme>;
}

export const MobileDatesFilterOptions = ({
  selectedDateRange,
  onChangeDateRange,
  onChangeEndDate,
  onChangeStartDate,
  startDate: customStartDate,
  endDate: customEndDate,
  sx,
}: Props) => {
  const areDatePickersEnabled = selectedDateRange === DATE_RANGE_OPTIONS.CUSTOM;
  const trackEvent = useTrackHomePageAnalytics();

  const startDate = getStartDate({
    dateRange: selectedDateRange,
    customStartDateValue: customStartDate,
  });
  const endDate = getEndDate({
    dateRange: selectedDateRange,
    customEndDateValue: customEndDate,
  });

  const handleDateRangeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newDateRange = event.target.value as DATE_RANGE_OPTIONS;

    trackEvent(TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect, {
      dateType: newDateRange,
    });

    onChangeDateRange(newDateRange);

    if (newDateRange !== DATE_RANGE_OPTIONS.CUSTOM) {
      onChangeStartDate(null);
      onChangeEndDate(null);
    }
  };

  return (
    <Stack
      sx={{
        flexDirection: 'column',
        gap: 1,
        marginBottom: 3,
        width: '100%',
        ...sx,
      }}
    >
      <TextField
        id="date-range"
        label="Transaction Created"
        fullWidth
        select
        value={selectedDateRange}
        onChange={handleDateRangeChange}
        LabelProps={{
          sx: { ...visuallyHidden },
        }}
        slotProps={{
          select: {
            labelId: 'date-range-label',
            MenuProps: {
              slotProps: {
                list: {
                  id: 'date-range-menu-list',
                },
              },
            },
          },
        }}
      >
        {DATE_RANGE_OPTIONS_LIST.map((option) => (
          <MenuItem key={option} value={option}>
            {DATE_RANGE_OPTION_LABELS[option]}
          </MenuItem>
        ))}
      </TextField>

      <Show when={areDatePickersEnabled}>
        <Stack sx={{ pl: 6.25, gap: 2 }}>
          <DatePickerWithEventTracking
            eventContainer="mobile-date-filter-dropdown-first"
            onAccept={(newDate) => {
              trackEvent(
                TrackHomePageEvent.FactsAndFiguresDateFilterStartDateInput,
                {
                  dateValue: newDate,
                },
              );
            }}
            onChange={(newDate) => {
              onChangeStartDate(newDate);
            }}
            slotProps={{
              textField: {
                label: 'Start Date',
                required: true,
                sx: { width: '100%' },
                onBlur: (event: FocusEvent<HTMLInputElement>) => {
                  const newDate = new Date(event.target.value);

                  if (isValid(newDate)) {
                    trackEvent(
                      TrackHomePageEvent.FactsAndFiguresDateFilterStartDateInput,
                      {
                        dateValue: newDate.toISOString(),
                      },
                    );
                  }
                },
              },
            }}
            value={startDate}
          />

          <DatePickerWithEventTracking
            eventContainer="mobile-date-filter-dropdown-second"
            onAccept={(newDate) => {
              trackEvent(
                TrackHomePageEvent.FactsAndFiguresDateFilterEndDateInput,
                {
                  dateValue: newDate,
                },
              );
            }}
            onChange={(newDate) => {
              onChangeEndDate(newDate);
            }}
            slotProps={{
              textField: {
                label: 'End Date',
                required: true,
                sx: { width: '100%' },
                onBlur: (event: FocusEvent<HTMLInputElement>) => {
                  const newDate = new Date(event.target.value);

                  if (isValid(newDate)) {
                    trackEvent(
                      TrackHomePageEvent.FactsAndFiguresDateFilterEndDateInput,
                      {
                        dateValue: newDate.toISOString(),
                      },
                    );
                  }
                },
              },
            }}
            value={endDate}
          />
        </Stack>
      </Show>
    </Stack>
  );
};
